import { useEffect } from 'react';
import { DEFAULT_PAGE_TITLE, DEFAULT_PAGE_DESCRIPTION } from '../shared/materialPresentation.mjs';

const releaseServerMetadata = () => {
  // Server tags describe the initial route, not defaults to restore on navigation.
  const title = document.head.querySelector('title[data-material-metadata], title[data-page-metadata]');
  if (title) {
    title.removeAttribute('data-material-metadata');
    title.removeAttribute('data-page-metadata');
    document.title = DEFAULT_PAGE_TITLE;
  }
  const description = document.head.querySelector('meta[name="description"][data-material-metadata], meta[name="description"][data-page-metadata]');
  if (description) {
    description.removeAttribute('data-material-metadata');
    description.removeAttribute('data-page-metadata');
    description.setAttribute('content', DEFAULT_PAGE_DESCRIPTION);
  }
  document.head.querySelectorAll('[data-page-metadata], link[rel="canonical"][data-material-metadata], meta[name="robots"][data-material-metadata]')
    .forEach(element => element.remove());
};

const ensureMetaDescription = () => {
  let metaDescription = document.querySelector('meta[name="description"]');

  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    document.head.appendChild(metaDescription);
  }

  return metaDescription;
};

// Undefined leaves a tag alone; null suppresses it for the current page.
const setOptionalMetadata = (tagName, key, name, attribute, value) => {
  if (value === undefined) return () => {};
  const selector = `${tagName}[${key}="${name}"]`;
  const existing = document.head.querySelector(selector);
  const previousValue = existing?.getAttribute(attribute);
  if (value === null) {
    existing?.remove();
    return () => { if (existing) document.head.appendChild(existing); };
  }
  const element = existing || document.createElement(tagName);
  element.setAttribute(key, name);
  element.setAttribute(attribute, value);
  if (!existing) document.head.appendChild(element);
  return () => {
    if (!existing) element.remove();
    else if (previousValue === null) element.removeAttribute(attribute);
    else element.setAttribute(attribute, previousValue);
  };
};

const setStructuredData = (id, value) => {
  if (value === undefined) return () => {};
  const existing = document.head.querySelector(`script#${id}[type="application/ld+json"]`);
  const previousValue = existing?.textContent;
  if (value === null) {
    existing?.remove();
    return () => { if (existing) document.head.appendChild(existing); };
  }
  const element = existing || document.createElement('script');
  element.id = id;
  element.type = 'application/ld+json';
  element.textContent = JSON.stringify(value);
  if (!existing) document.head.appendChild(element);
  return () => {
    if (!existing) element.remove();
    else element.textContent = previousValue;
  };
};

const usePageMetadata = (title, description, {
  canonical,
  robots,
  openGraph = {},
  twitter = {},
  structuredData,
  structuredDataId = 'page-structured-data',
} = {}) => {
  useEffect(() => {
    releaseServerMetadata();
    const previousTitle = document.title;
    const metaDescription = ensureMetaDescription();
    const previousDescription = metaDescription.getAttribute('content');

    document.title = title;
    metaDescription.setAttribute('content', description);
    const restores = [
      setOptionalMetadata('link', 'rel', 'canonical', 'href', canonical),
      setOptionalMetadata('meta', 'name', 'robots', 'content', robots),
      setOptionalMetadata('meta', 'property', 'og:title', 'content', openGraph.title),
      setOptionalMetadata('meta', 'property', 'og:description', 'content', openGraph.description),
      setOptionalMetadata('meta', 'property', 'og:url', 'content', openGraph.url),
      setOptionalMetadata('meta', 'property', 'og:type', 'content', openGraph.type),
      setOptionalMetadata('meta', 'property', 'og:site_name', 'content', openGraph.siteName),
      setOptionalMetadata('meta', 'name', 'twitter:card', 'content', twitter.card),
      setOptionalMetadata('meta', 'name', 'twitter:title', 'content', twitter.title),
      setOptionalMetadata('meta', 'name', 'twitter:description', 'content', twitter.description),
      setStructuredData(structuredDataId, structuredData),
    ];

    return () => {
      restores.reverse().forEach(restore => restore());
      document.title = previousTitle;
      if (previousDescription === null) {
        metaDescription.removeAttribute('content');
      } else {
        metaDescription.setAttribute('content', previousDescription);
      }
    };
  }, [
    description, title, canonical, robots,
    openGraph.title, openGraph.description, openGraph.url, openGraph.type, openGraph.siteName,
    twitter.card, twitter.title, twitter.description,
    structuredData, structuredDataId,
  ]);
};

export default usePageMetadata;
